import React, { useState } from 'react';
import { Target, TrendingUp, CheckCircle2, Trash2, Edit2, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Metric target types with descriptions
const METRIC_TARGETS = [
  { value: 'green_percentage', label: 'Green Rate %', description: 'Percentage of items completed in under 5 minutes', unit: '%' },
  { value: 'avg_completion_time', label: 'Avg Completion Time', description: 'Average time to complete items (in seconds)', unit: 'sec' },
  { value: 'total_items', label: 'Total Items', description: 'Total number of items completed', unit: 'items' },
  { value: 'red_percentage', label: 'Red Rate % (Lower is better)', description: 'Percentage of items taking over 15 minutes', unit: '%' },
];

/**
 * Goals Section - Manage goals including target-based metric goals with edit/delete
 */
export default function GoalsSection({ reviewData, onGoalAdded, onGoalUpdated, onGoalDeleted, metrics }) {
  const [goalType, setGoalType] = useState('general'); // 'general' or 'metric_target'
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editGoalData, setEditGoalData] = useState(null);
  const [deletingGoalId, setDeletingGoalId] = useState(null);
  const [newGoal, setNewGoal] = useState({ 
    title: '', 
    description: '', 
    target_date: '', 
    priority: 'medium',
    metric_type: 'green_percentage',
    target_value: '',
    comparison: 'gte' // gte = greater than or equal, lte = less than or equal
  });

  const handleAddGoal = async () => {
    if (!newGoal.title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    
    if (goalType === 'metric_target' && !newGoal.target_value) {
      toast.error('Please enter a target value');
      return;
    }

    try {
      const res = await api.post('/admin/performance/reviews/goals', {
        review_id: reviewData.id,
        goal_title: newGoal.title,
        goal_description: newGoal.description,
        target_date: newGoal.target_date || null,
        priority: newGoal.priority,
        goal_type: goalType,
        metric_type: goalType === 'metric_target' ? newGoal.metric_type : null,
        target_value: goalType === 'metric_target' ? parseFloat(newGoal.target_value) : null,
        comparison: goalType === 'metric_target' ? newGoal.comparison : null
      });
      
      if (res.data.success) {
        onGoalAdded(res.data.goal);
        setNewGoal({ 
          title: '', 
          description: '', 
          target_date: '', 
          priority: 'medium',
          metric_type: 'green_percentage',
          target_value: '',
          comparison: 'gte'
        });
        setGoalType('general');
        toast.success('Goal added');
      }
    } catch (err) {
      toast.error('Failed to add goal');
    }
  };

  // Handle edit goal - start editing mode
  const handleStartEdit = (goal) => {
    setEditingGoalId(goal.id);
    setEditGoalData({
      title: goal.title,
      description: goal.description || '',
      target_date: goal.target_date || '',
      priority: goal.priority || 'medium',
      status: goal.status || 'pending'
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingGoalId(null);
    setEditGoalData(null);
  };

  // Handle save edited goal
  const handleSaveEdit = async (goalId) => {
    if (!editGoalData.title.trim()) {
      toast.error('Goal title is required');
      return;
    }

    try {
      const res = await api.put(`/admin/performance/reviews/goals/${reviewData.id}/${goalId}`, editGoalData);
      
      if (res.data.success) {
        if (onGoalUpdated) {
          onGoalUpdated(goalId, editGoalData);
        }
        setEditingGoalId(null);
        setEditGoalData(null);
        toast.success('Goal updated');
      }
    } catch (err) {
      toast.error('Failed to update goal');
    }
  };

  // Handle delete goal
  const handleDeleteGoal = async (goalId) => {
    setDeletingGoalId(goalId);
    try {
      const res = await api.delete(`/admin/performance/reviews/goals/${reviewData.id}/${goalId}`);
      
      if (res.data.success) {
        if (onGoalDeleted) {
          onGoalDeleted(goalId);
        }
        toast.success('Goal deleted');
      }
    } catch (err) {
      toast.error('Failed to delete goal');
    } finally {
      setDeletingGoalId(null);
    }
  };

  // Calculate actual value for a metric target goal
  const getActualValue = (goal) => {
    if (!metrics || !goal.metric_type) return null;
    
    const total = metrics.total_items_completed || 0;
    if (total === 0) return 0;
    
    switch (goal.metric_type) {
      case 'green_percentage':
        return Math.round((metrics.by_color.green / total) * 100);
      case 'red_percentage':
        return Math.round((metrics.by_color.red / total) * 100);
      case 'avg_completion_time':
        return metrics.average_completion_seconds || 0;
      case 'total_items':
        return total;
      default:
        return null;
    }
  };

  // Check if goal is achieved
  const isGoalAchieved = (goal, actualValue) => {
    if (actualValue === null || goal.target_value === null) return null;
    
    if (goal.comparison === 'lte') {
      return actualValue <= goal.target_value;
    } else {
      return actualValue >= goal.target_value;
    }
  };

  // Calculate progress bar width - handles both GTE and LTE goals
  const calculateProgressWidth = (actual, target, comparison) => {
    if (actual === null || target === null || target === 0) return 0;
    
    if (comparison === 'lte') {
      // For "less than or equal" goals (e.g., Red Rate ≤ 10%):
      // If actual is 5% and target is 10%, we're doing well -> show 100%
      // If actual is 15% and target is 10%, we're over -> show capped amount
      if (actual <= target) {
        return 100; // Goal achieved
      }
      // Over target - show how much over (capped at 100)
      return Math.min(100, (target / actual) * 100);
    } else {
      // For "greater than or equal" goals (e.g., Green Rate ≥ 80%):
      return Math.min(100, ((actual || 0) / Math.max(target, 1)) * 100);
    }
  };

  // Get metric unit
  const getMetricUnit = (metricType) => {
    const metric = METRIC_TARGETS.find(m => m.value === metricType);
    return metric?.unit || '';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Target className="w-5 h-5" />
        Goals & Objectives
      </h3>
      
      {/* Existing Goals */}
      {reviewData.goals && reviewData.goals.length > 0 && (
        <div className="space-y-3 mb-4">
          {reviewData.goals.map((goal) => {
            const isMetricGoal = goal.goal_type === 'metric_target';
            const actualValue = isMetricGoal ? getActualValue(goal) : null;
            const achieved = isMetricGoal ? isGoalAchieved(goal, actualValue) : null;
            const isEditing = editingGoalId === goal.id;
            const isDeleting = deletingGoalId === goal.id;
            
            return (
              <div key={goal.id} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg group">
                {isEditing ? (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <Input
                      value={editGoalData.title}
                      onChange={(e) => setEditGoalData(d => ({ ...d, title: e.target.value }))}
                      placeholder="Goal title"
                      className="font-medium"
                    />
                    <Input
                      value={editGoalData.description}
                      onChange={(e) => setEditGoalData(d => ({ ...d, description: e.target.value }))}
                      placeholder="Description"
                    />
                    <div className="flex gap-2 flex-wrap">
                      <Input
                        type="date"
                        value={editGoalData.target_date}
                        onChange={(e) => setEditGoalData(d => ({ ...d, target_date: e.target.value }))}
                        className="flex-1 min-w-[140px]"
                      />
                      <select
                        value={editGoalData.priority}
                        onChange={(e) => setEditGoalData(d => ({ ...d, priority: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      <select
                        value={editGoalData.status}
                        onChange={(e) => setEditGoalData(d => ({ ...d, status: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                        <X className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={() => handleSaveEdit(goal.id)} className="bg-teal-600 hover:bg-teal-700 text-white">
                        <Save className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 dark:text-white">{goal.title}</p>
                        {isMetricGoal && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            Metric Target
                          </span>
                        )}
                        {goal.status === 'completed' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Completed
                          </span>
                        )}
                        {goal.status === 'in_progress' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            In Progress
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{goal.description}</p>
                      
                      {/* Metric Target Progress */}
                      {isMetricGoal && metrics && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-600 dark:text-gray-400">
                              Target: {goal.comparison === 'lte' ? '≤' : '≥'} {goal.target_value}{getMetricUnit(goal.metric_type)}
                            </span>
                            <span className={`font-medium ${achieved ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                              Actual: {actualValue}{getMetricUnit(goal.metric_type)}
                            </span>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${achieved ? 'bg-green-500' : 'bg-amber-500'}`}
                              style={{ 
                                width: `${calculateProgressWidth(actualValue, goal.target_value, goal.comparison)}%` 
                              }}
                            />
                          </div>
                          
                          {/* Achievement Badge */}
                          <div className="flex items-center gap-1 mt-2">
                            {achieved ? (
                              <>
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                <span className="text-xs text-green-600 dark:text-green-400 font-medium">Goal Achieved!</span>
                              </>
                            ) : (
                              <>
                                <TrendingUp className="w-4 h-4 text-amber-500" />
                                <span className="text-xs text-amber-600 dark:text-amber-400">In Progress</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {goal.target_date && (
                        <p className="text-xs text-gray-500 mt-2">Target: {new Date(goal.target_date).toLocaleDateString()}</p>
                      )}
                    </div>
                    
                    {/* Priority badge and action buttons */}
                    <div className="flex items-start gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                        goal.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        goal.priority === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                      }`}>
                        {goal.priority}
                      </span>
                      
                      {/* Edit/Delete buttons - show on hover */}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleStartEdit(goal)}
                          className="p-1.5 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 rounded transition-colors"
                          title="Edit goal"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteGoal(goal.id)}
                          disabled={isDeleting}
                          className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                          title="Delete goal"
                        >
                          <Trash2 className={`w-4 h-4 ${isDeleting ? 'animate-pulse' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Add Goal Form */}
      <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        {/* Goal Type Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setGoalType('general')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              goalType === 'general' 
                ? 'bg-teal-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
            }`}
          >
            General Goal
          </button>
          <button
            onClick={() => setGoalType('metric_target')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              goalType === 'metric_target' 
                ? 'bg-teal-600 text-white' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-1" />
            Metric Target
          </button>
        </div>

        <Input
          value={newGoal.title}
          onChange={(e) => setNewGoal(g => ({ ...g, title: e.target.value }))}
          placeholder={goalType === 'metric_target' ? 'e.g., Improve green completion rate' : 'Goal title'}
        />
        
        <Input
          value={newGoal.description}
          onChange={(e) => setNewGoal(g => ({ ...g, description: e.target.value }))}
          placeholder="Goal description"
        />

        {/* Metric Target Fields */}
        {goalType === 'metric_target' && (
          <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Metric Target Settings</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-blue-700 dark:text-blue-400 mb-1">Metric Type</label>
                <select
                  value={newGoal.metric_type}
                  onChange={(e) => setNewGoal(g => ({ ...g, metric_type: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {METRIC_TARGETS.map(metric => (
                    <option key={metric.value} value={metric.value}>{metric.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-blue-700 dark:text-blue-400 mb-1">Comparison</label>
                <select
                  value={newGoal.comparison}
                  onChange={(e) => setNewGoal(g => ({ ...g, comparison: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="gte">Greater than or equal (≥)</option>
                  <option value="lte">Less than or equal (≤)</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-blue-700 dark:text-blue-400 mb-1">
                Target Value ({METRIC_TARGETS.find(m => m.value === newGoal.metric_type)?.unit})
              </label>
              <Input
                type="number"
                value={newGoal.target_value}
                onChange={(e) => setNewGoal(g => ({ ...g, target_value: e.target.value }))}
                placeholder={`e.g., ${newGoal.metric_type === 'green_percentage' ? '80' : newGoal.metric_type === 'avg_completion_time' ? '240' : '100'}`}
                className="border-blue-300 dark:border-blue-700"
              />
            </div>
            
            <p className="text-xs text-blue-600 dark:text-blue-400">
              {METRIC_TARGETS.find(m => m.value === newGoal.metric_type)?.description}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Input
            type="date"
            value={newGoal.target_date}
            onChange={(e) => setNewGoal(g => ({ ...g, target_date: e.target.value }))}
            className="flex-1 min-w-[140px]"
          />
          <select
            value={newGoal.priority}
            onChange={(e) => setNewGoal(g => ({ ...g, priority: e.target.value }))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="low">Low Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="high">High Priority</option>
          </select>
          <Button onClick={handleAddGoal} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-goal-btn">
            Add Goal
          </Button>
        </div>
      </div>
    </div>
  );
}
